/**
 * The admin / settings menu, in one place. The settings overview, the side menu and the mobile
 * menu all read this list, so a new admin page is added here once. Addresses are the existing
 * ones (bookmarks and e-mail links keep working); the logbook admin areas are one page with a
 * ?tab= address.
 */

export type AdminGroupKey = "masterData" | "bookings" | "communication" | "logbook" | "system";

export interface AdminNavItem {
  /** Translation key suffix: nav.admin.items.<key> (and .desc.<key>) */
  key: string;
  path: string;
  /** For the logbook admin page: which tab the item opens */
  tab?: string;
  /** The label comes from this existing translation key instead of nav.admin.items.<key> */
  labelKey?: string;
}

export interface AdminNavGroup {
  key: AdminGroupKey;
  items: AdminNavItem[];
}

export const LOGBOOK_TABS = ["compliance", "rules", "library", "engines", "fuel", "vessels", "crew", "equipment", "documents", "drills", "journal"] as const;
export type LogbookTab = (typeof LOGBOOK_TABS)[number];
export const DEFAULT_LOGBOOK_TAB: LogbookTab = "compliance";

export const ADMIN_NAV: AdminNavGroup[] = [
  {
    key: "masterData",
    items: [
      { key: "partners", path: "/admin/partners" },
      { key: "articles", path: "/admin/articles" },
      { key: "company", path: "/admin/company" },
      { key: "images", path: "/admin/images" },
      { key: "fortnox", path: "/admin/fortnox" },
    ],
  },
  {
    key: "bookings",
    items: [
      { key: "trips", path: "/admin/trips" },
      { key: "schedule", path: "/admin/schedule" },
      { key: "locations", path: "/admin/locations" },
      { key: "reports", path: "/admin/reports" },
      { key: "shareLinks", path: "/admin/share-links" },
    ],
  },
  {
    key: "communication",
    items: [{ key: "emailTemplates", path: "/admin/email-templates" }],
  },
  {
    key: "logbook",
    items: [
      ...LOGBOOK_TABS.filter(tab => !["engines", "fuel", "compliance", "drills", "journal", "rules", "library", "equipment", "documents"].includes(tab)).map((tab) => ({ key: `logbook-${tab}`, path: `/admin/logbook?tab=${tab}`, tab, labelKey: `logbook.admin.${tab}` })),
    ],
  },
  { key: "system", items: [
    { key: "logbook-company", path: "/admin/system/logbook?tab=company", tab: "company", labelKey: "logbook.workflow.companyProcedures" },
    { key: "logbook-library", path: "/admin/system/logbook?tab=library", tab: "library", labelKey: "logbook.admin.library" },
    { key: "auditLogs", path: "/admin/audit-logs" },
  ] },
];

export const adminNavItems = (): AdminNavItem[] => ADMIN_NAV.flatMap((g) => g.items);

/** The tab asked for in a ?tab= value, or the default when it is missing or unknown. */
export const logbookTabFrom = (value: string | null | undefined): LogbookTab => (LOGBOOK_TABS as readonly string[]).includes(value ?? "") ? (value as LogbookTab) : DEFAULT_LOGBOOK_TAB;

/** Is this menu item the page being shown? */
export function isAdminNavActive(item: AdminNavItem, pathname: string, search: string): boolean {
  const base = item.path.split("?")[0];
  if (pathname !== base) return false;
  if (!item.tab) return true;
  const rawTab = new URLSearchParams(search).get("tab");
  const tab = (item.tab === "rules" || item.tab === "library" || item.tab === "company") && rawTab === item.tab ? rawTab : logbookTabFrom(rawTab);
  const section = new URLSearchParams(search).get("section");
  if (item.tab === "ekan") return tab === "vessels" && section === "ekan";
  if (item.tab === "vessels" && section === "ekan") return false;
  return (tab === "engines" || tab === "fuel" ? "vessels" : tab) === item.tab;
}

/** The group the current page belongs to (its items are shown open in the side menu). */
export function activeAdminGroup(pathname: string, search: string): AdminGroupKey | undefined {
  return ADMIN_NAV.find((g) => g.items.some((i) => isAdminNavActive(i, pathname, search)))?.key;
}
